import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Calendar, 
  MapPin, 
  Trophy, 
  Award,
  User
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { fetchResultById } from '../services/resultsApi';

const ResultDetailPage = () => {
  const { isDarkMode } = useTheme();
  const { slug } = useParams();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadResult();
  }, [slug]);

  const loadResult = async () => {
    try {
      setLoading(true);
      const data = await fetchResultById(slug);
      setResult(data);
    } catch (err) {
      console.error('Error loading result:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-ZA', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        isDarkMode ? 'bg-fsgu-gray-900' : 'bg-fsgu-silver-50'
      }`}>
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-fsgu-gold-500 mb-4"></div>
          <p className={isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'}>
            Loading result...
          </p>
        </div>
      </div>
    );
  }

  if (error || !result) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        isDarkMode ? 'bg-fsgu-gray-900' : 'bg-fsgu-silver-50'
      }`}>
        <div className="text-center">
          <Trophy className={`w-16 h-16 mx-auto mb-4 ${
            isDarkMode ? 'text-fsgu-silver-600' : 'text-fsgu-silver-400'
          }`} />
          <h2 className={`text-2xl font-bold mb-2 ${
            isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
          }`}>
            Result Not Found
          </h2>
          <Link
            to="/results"
            className="btn btn-primary inline-flex items-center mt-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Results
          </Link>
        </div>
      </div>
    );
  }

  const getCategoryColor = (category) => {
    const colors = {
      "Men's Open": 'blue',
      "Men's Mid-Am": 'blue',
      "Men's Senior": 'purple',
      'Junior Boys': 'green',
      "Ladies Open": 'pink',
      "Ladies Mid-Am": 'pink',
      "Ladies Senior": 'purple',
      'Junior Girls': 'green',
      'Mixed': 'gold',
      'Open': 'gold'
    };
    return colors[category] || 'blue';
  };

  const categoryColor = getCategoryColor(result.category);

  return (
    <div className={`min-h-screen ${
      isDarkMode ? 'bg-fsgu-gray-900' : 'bg-fsgu-silver-50'
    }`}>
      {/* Hero Section with Image */}
      {result.featured_image_url && (
        <div className="relative h-[60vh] overflow-hidden">
          <img
            src={result.featured_image_url}
            alt={result.tournament_name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
          
          {/* Hero Content */}
          <div className="absolute inset-0 flex items-end">
            <div className="container-custom pb-12">
              {/* Category Badge */}
              <div className="mb-4">
                <span className={`inline-block px-4 py-1.5 rounded-full text-sm font-medium bg-fsgu-${categoryColor}-500 text-white`}>
                  {result.category}
                </span>
              </div>

              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
                {result.report_title || result.tournament_name}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-white/90">
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  <span>{formatDate(result.tournament_date)}</span>
                </div>
                
                {result.venue && (
                  <>
                    <span className="text-white/40">•</span>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-5 h-5" />
                      <span>{result.venue}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Back Button */}
      <div className="container-custom py-6">
        <div className="flex items-center justify-between">
          <Link
            to="/results"
            className={`inline-flex items-center px-4 py-2 rounded-lg font-medium transition-all ${
              isDarkMode
                ? 'text-fsgu-silver-300 hover:bg-fsgu-gray-800'
                : 'text-fsgu-silver-700 hover:bg-fsgu-silver-100'
            }`}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Results
          </Link>
        </div>
      </div>

      {/* Main Content */}
      <div className="container-custom pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Winner Spotlight */}
            <div className={`p-8 rounded-xl border mb-8 ${
              isDarkMode
                ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
                : 'bg-fsgu-white border-fsgu-silver-200'
            }`}>
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-full bg-fsgu-gold-500/20">
                  <Trophy className="w-6 h-6 text-fsgu-gold-500" />
                </div>
                <h2 className={`text-2xl font-bold ${
                  isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
                }`}>
                  Champion
                </h2>
              </div>

              <div className="space-y-4">
                <div>
                  <div className={`text-sm mb-1 ${
                    isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
                  }`}>
                    Winner
                  </div>
                  <div className={`text-2xl font-bold ${
                    isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
                  }`}>
                    {result.winner_name}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {result.winner_score && (
                    <div>
                      <div className={`text-sm mb-1 ${
                        isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
                      }`}>
                        Score
                      </div>
                      <div className={`text-xl font-semibold text-fsgu-gold-500`}>
                        {result.winner_score}
                      </div>
                    </div>
                  )}

                  {result.winner_club && (
                    <div>
                      <div className={`text-sm mb-1 ${
                        isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
                      }`}>
                        Club
                      </div>
                      <div className={`text-lg font-medium ${
                        isDarkMode ? 'text-fsgu-silver-200' : 'text-fsgu-silver-900'
                      }`}>
                        {result.winner_club}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Report Content */}
            {result.report_content && (
              <div className={`p-8 rounded-xl border ${
                isDarkMode
                  ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
                  : 'bg-fsgu-white border-fsgu-silver-200'
              }`}>
                <div 
                  className={`prose prose-lg max-w-none ${
                    isDarkMode 
                      ? 'prose-invert prose-p:text-fsgu-silver-300 prose-headings:text-fsgu-silver-100' 
                      : 'prose-p:text-fsgu-silver-700 prose-headings:text-fsgu-silver-900'
                  }`}
                  dangerouslySetInnerHTML={{ __html: result.report_content }}
                />

                {result.report_author && (
                  <div className={`mt-6 pt-6 border-t flex items-center gap-2 ${
                    isDarkMode ? 'border-fsgu-gray-700' : 'border-fsgu-silver-200'
                  }`}>
                    <User className={`w-4 h-4 ${
                      isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-400'
                    }`} />
                    <span className={`text-sm ${
                      isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                    }`}>
                      Report by {result.report_author}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Tournament Details */}
            <div className={`p-6 rounded-xl border ${
              isDarkMode
                ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
                : 'bg-fsgu-white border-fsgu-silver-200'
            }`}>
              <h3 className={`text-lg font-semibold mb-4 ${
                isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
              }`}>
                Tournament Details
              </h3>

              <div className="space-y-3">
                <div>
                  <div className={`text-sm mb-1 ${
                    isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
                  }`}>
                    Tournament
                  </div>
                  <div className={`font-medium ${
                    isDarkMode ? 'text-fsgu-silver-200' : 'text-fsgu-silver-900'
                  }`}>
                    {result.tournament_name}
                  </div>
                </div>

                <div>
                  <div className={`text-sm mb-1 ${
                    isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
                  }`}>
                    Date
                  </div>
                  <div className={`font-medium ${
                    isDarkMode ? 'text-fsgu-silver-200' : 'text-fsgu-silver-900'
                  }`}>
                    {formatDate(result.tournament_date)}
                  </div>
                </div>

                {result.venue && (
                  <div>
                    <div className={`text-sm mb-1 ${
                      isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
                    }`}>
                      Venue
                    </div>
                    <div className={`font-medium ${
                      isDarkMode ? 'text-fsgu-silver-200' : 'text-fsgu-silver-900'
                    }`}>
                      {result.venue}
                    </div>
                  </div>
                )}

                {result.format && (
                  <div>
                    <div className={`text-sm mb-1 ${
                      isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
                    }`}>
                      Format
                    </div>
                    <div className={`font-medium ${
                      isDarkMode ? 'text-fsgu-silver-200' : 'text-fsgu-silver-900'
                    }`}>
                      {result.format}
                    </div>
                  </div>
                )}

                <div>
                  <div className={`text-sm mb-1 ${
                    isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
                  }`}>
                    Category
                  </div>
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium bg-fsgu-${categoryColor}-500 text-white`}>
                    {result.category}
                  </span>
                </div>
              </div>
            </div>

            {/* View More Results CTA */}
            <Link
              to="/results"
              className={`block p-6 rounded-xl border text-center group transition-all ${
                isDarkMode
                  ? 'bg-fsgu-gray-800 border-fsgu-gray-700 hover:border-fsgu-gold-500/50'
                  : 'bg-fsgu-white border-fsgu-silver-200 hover:border-fsgu-gold-500/50'
              }`}
            >
              <Award className={`w-8 h-8 mx-auto mb-3 ${
                isDarkMode ? 'text-fsgu-gold-400' : 'text-fsgu-gold-500'
              }`} />
              <h4 className={`text-lg font-semibold mb-2 ${
                isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
              }`}>
                View More Results
              </h4>
              <p className={`text-sm ${
                isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
              }`}>
                Explore other tournament results
              </p>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResultDetailPage;